// Creditos.js — a loja de Tickets e Insigne Coins, dentro do perfil.
//
// ================= AS DUAS MOEDAS =================
//
// TICKET compra tempo de Clube: 1 Ticket = 30 dias, e os dias somam. Desde
// 10/09/2026 é a única porta de entrada do Clube — a assinatura direta saiu
// porque obrigava a pessoa a voltar no dia certo, todo mês, ou perder o acesso.
//
// COIN é o troco do site (avaliação, compra, perfil preenchido) e vale desconto
// no checkout. Aqui ela ganha um segundo uso: comprar Ticket sem cartão.
//
// ================= POR QUE VERMELHO E LARANJA =================
//
// As duas moedas se distinguem pela COR antes de qualquer leitura: Ticket em
// `insigne-ticket` (vermelho), Coin em `insigne-orange`. Foi pedido do dono, e
// resolve o problema real de duas prateleiras empilhadas na mesma tela — sem a
// cor, "3" e "150" a dois dedos de distância exigem ler o rótulo para saber do
// que se está falando.
//
// E O NOME DA MOEDA TAMBÉM LEVA A COR, com inicial maiúscula (pedido de
// 11/09/2026): "Tickets" e "Coins" são nomes próprios dos dois créditos, não
// palavras soltas da frase. Por isso eles saem sempre por <Ticket> e <Coin>,
// abaixo — escrever a palavra à mão num canto desta tela é o jeito de ela
// voltar minúscula e branca sem ninguém perceber.
//
// ================= O PREÇO NÃO NASCE AQUI =================
//
// Esta tela manda QUAL pacote, e só. O valor sai da tabela `credito_pacotes` no
// servidor. É a mesma regra do PrecoService, pelo mesmo motivo: preço que viaja
// no request é preço que qualquer um edita na aba de rede — e aqui o produto é
// dinheiro, o que torna a brecha imediata.
import React, { useCallback, useEffect, useState } from "react";
import { message } from "../../utils/toast";
import TarjaDesconto from "../../ui/TarjaDesconto";
import useConfirmarGasto, { Tickets, Coins } from "../../ui/ConfirmarGasto";

const API_URL = process.env.REACT_APP_API;
const tokenKey = "token" + (process.env.REACT_APP_USER_FUNCTION || "");

const reais = (centavos) =>
  "R$ " + ((Number(centavos) || 0) / 100).toFixed(2).replace(".", ",");

/** O nome do crédito, na cor dele. `plural` decide "Ticket" ou "Tickets". */
const Ticket = ({ plural = true }) => (
  <span className="font-semibold text-insigne-ticket">{plural ? "Tickets" : "Ticket"}</span>
);
const Coin = ({ plural = true }) => (
  <span className="font-semibold text-insigne-orange">{plural ? "Coins" : "Coin"}</span>
);

// ================= O DESTAQUE DO "MELHOR CUSTO" =================
//
// O mesmo da página do Clube (pedido de 11/09/2026): borda e fundo na cor do
// crédito, e o selo no alto. Aqui a sombra dura dos cards também troca de cor —
// é o acabamento desta prateleira, e a sombra colorida é o que faz o card
// "saltar" da fileira do mesmo jeito que o brilho faz lá.
//
// Classes ESCRITAS POR INTEIRO, e não montadas com `${cor}`: o Tailwind só gera
// as classes que encontra literalmente no código, e `border-insigne-${x}`
// passaria pelo build sem existir no CSS.
const DESTAQUE = {
  // Vazio: a borda, o fundo e a sombra dura do card comum são o `.cartao` do
  // App.css — esta prateleira foi o card que virou o padrão do site.
  normal: "",
  ticket: {
    card:
      "border-insigne-ticket bg-insigne-ticket/[0.12] shadow-[4px_4px_0_0_rgb(var(--c-ticket)/0.55)] " +
      "hover:shadow-[6px_6px_0_0_rgb(var(--c-ticket)/0.55)]",
    // O texto do selo mora AQUI, e não mais fixo como `text-paper` no JSX:
    // com o Clube em branco (17/09/2026), branco sobre branco sumiria. Cada
    // moeda diz a cor do próprio texto.
    selo: "bg-insigne-ticket text-insigne-ticket-contraste",
  },
  coin: {
    card:
      "border-insigne-orange bg-insigne-orange/[0.12] shadow-[4px_4px_0_0_rgb(var(--c-orange)/0.55)] " +
      "hover:shadow-[6px_6px_0_0_rgb(var(--c-orange)/0.55)]",
    selo: "bg-insigne-orange text-paper",
  },
};

/**
 * O id do pacote com o MENOR preço por unidade (centavos por Ticket, ou por
 * Coin). CALCULADO, e não "o maior pacote": se o dono mudar os preços no
 * painel, o selo tem de ir sozinho para quem de fato for o melhor. Com um
 * pacote só não há "melhor" — o selo diria algo sobre nada.
 */
const idDoMelhor = (lista, unidades) => {
  if (!Array.isArray(lista) || lista.length < 2) return null;
  let melhor = null;
  lista.forEach((p) => {
    const n = Number(unidades(p)) || 0;
    if (n <= 0) return;
    const porUnidade = (Number(p.centavos) || 0) / n;
    if (!melhor || porUnidade < melhor.porUnidade) melhor = { id: p.id, porUnidade };
  });
  return melhor?.id ?? null;
};

/**
 * Um card da prateleira.
 *
 * O DESCONTO VEM PRONTO DO SERVIDOR (`desconto_percentual`), calculado a partir
 * dos dois preços que este card imprime. Recalcular aqui abriria a porta para a
 * tarja discordar do riscado ao lado do preço.
 */
const Card = ({
  icone,
  cor,
  quantidade,
  rotulo,
  detalhe,
  centavos,
  deCentavos,
  pct,
  indiceTarja,
  melhor,
  corDestaque,
  aoComprar,
  precoCoins,
  aoComprarComCoins,
  podeComCoins,
  ocupado,
}) => (
  <div
    className={
      "cartao cartao-clique relative flex flex-col px-2.5 pt-4 pb-3 " +
      (melhor ? DESTAQUE[corDestaque].card : DESTAQUE.normal)
    }
  >
    {/* A tarja de desconto, maior e inclinada — a mesma da página do Clube. */}
    <TarjaDesconto pct={pct} indice={indiceTarja} />

    {/* O selo "Melhor custo", como na página do Clube — só que PENDURADO NA
        BORDA DE BAIXO. Em cima não cabe: este card tem ~160px de largura, a
        tarja de desconto ocupa ~80px do canto esquerdo e o selo mede ~100px,
        então no alto os dois se sobrepunham em qualquer posição. Embaixo ele
        não disputa lugar com nada, e não aumenta a altura do card (é
        `absolute`; a folga vem do `gap-y` da grade). */}
    {melhor && (
      <span className={"absolute -bottom-2.5 left-1/2 -translate-x-1/2 z-10 whitespace-nowrap rounded-selo px-2 py-0.5 text-[10px] " +
                       "font-extrabold uppercase tracking-wide " + DESTAQUE[corDestaque].selo}>
        Melhor custo
      </span>
    )}

    {/* ===== ÍCONE, QUANTIDADE E NOME NUMA LINHA SÓ =====
        "🎟 12 Tickets", lido de uma vez (pedido de 11/09/2026). O nome vem À
        DIREITA do número, menor que ele e na mesma cor, alinhado pela linha de
        base — alinhado pelo centro, o "Tickets" de 16px pareceria flutuar ao
        lado do "12" de 26px. Embaixo sobra só o prazo, e o card de Coins, que
        não tem prazo, fica uma linha mais baixo. */}
    <div className="flex flex-col items-center">
      <p className={`flex flex-wrap items-baseline justify-center gap-x-1.5 font-extrabold leading-none ${cor}`}>
        <i className={`bi ${icone} self-center text-xl md:text-[22px]`} aria-hidden="true" />
        <span className="text-2xl md:text-[26px]">{quantidade}</span>
        <span className="text-sm md:text-base">{rotulo}</span>
      </p>
      {detalhe && <p className="mt-1.5 text-xs text-white/50 text-center">{detalhe}</p>}
    </div>

    {/* ===== O PREÇO: cobrado, e o real À DIREITA, menor =====
        Pedido de 11/09/2026. O riscado ficava EM CIMA do botão; ao lado do
        valor cobrado os dois são lidos como uma coisa só ("125, era 150"). */}
    <p className="mt-3 flex items-baseline justify-center gap-1.5 flex-wrap">
      <span className="text-lg md:text-xl font-extrabold text-white leading-none">{reais(centavos)}</span>
      {deCentavos > centavos && (
        <span className="text-xs text-white/40 line-through">
          <span className="sr-only">de </span>
          {reais(deCentavos)}
        </span>
      )}
    </p>

    {/* ===== OS DOIS BOTÕES, LADO A LADO =====
        R$ à esquerda (o caminho principal, sólido) e Coins à direita, VAZADO
        com a borda laranja: é a alternativa, e não pode competir com o botão
        principal pelo olho. `flex-wrap` só como rede: se um dia a coluna ficar
        estreita demais para os dois, o de Coins desce em vez de vazar do card.

        AS MEDIDAS SÃO CONTADAS: numa tela de ~1100px o card tem ~140px por
        dentro, e "Comprar" + "1250" cabem nisso com 13px de fonte, 6px de
        respiro lateral e 4px entre os dois. Aumentar qualquer um deles faz o
        botão de Coins cair para a linha de baixo justamente nessa largura. */}
    <div className="mt-3 flex flex-wrap gap-1">
      <button
        type="button"
        onClick={aoComprar}
        disabled={ocupado}
        // Nos pacotes de TICKET o botão é da cor do Clube (branco, desde
        // 17/09/2026); nos de Coin continua o laranja, que é a cor da Coin.
        // O de Coins ao lado segue laranja nos dois: ele paga COM Coins.
        className={
          "btn-solido flex-1 whitespace-nowrap rounded-controle px-1.5 py-2 text-[13px] font-bold disabled:opacity-60" +
          (corDestaque === "ticket" ? " btn-clube" : "")
        }
      >
        Comprar
      </button>

      {/* O caminho sem cartão. Só no Ticket: Coin não se compra com Coin. */}
      {precoCoins != null && (
        <button
          type="button"
          onClick={aoComprarComCoins}
          disabled={ocupado || !podeComCoins}
          title={podeComCoins ? `Comprar com ${precoCoins} Coins` : "Saldo de Coins insuficiente"}
          aria-label={`Comprar com ${precoCoins} Coins`}
          className="flex-1 inline-flex items-center justify-center gap-1 whitespace-nowrap rounded-controle
                     border-2 border-insigne-orange bg-transparent px-1.5 py-2 text-[13px] font-bold
                     text-insigne-orange transition-colors hover:bg-insigne-orange/10
                     disabled:opacity-40 disabled:hover:bg-transparent"
        >
          <i className="bi bi-coin" aria-hidden="true" /> {precoCoins}
        </button>
      )}
    </div>
  </div>
);

const Creditos = () => {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState("");
  const { confirmar, dialogo } = useConfirmarGasto();

  const carregar = useCallback(async () => {
    const token = localStorage.getItem(tokenKey);
    if (!token) {
      setErro("Entre na sua conta para comprar créditos.");
      setCarregando(false);
      return;
    }
    try {
      const r = await fetch(API_URL + "creditos", {
        headers: { Authorization: "Bearer " + token },
      });
      if (!r.ok) throw new Error("Não foi possível carregar a loja de créditos.");
      setDados(await r.json());
      setErro("");
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const comprar = async (tipo, quantidade) => {
    const token = localStorage.getItem(tokenKey);
    if (!token) return message.error("Entre na sua conta para comprar.");
    setOcupado(true);
    try {
      const r = await fetch(API_URL + "creditos/comprar", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ tipo, quantidade }),
      });
      const d = await r.json();
      if (!r.ok || !d.checkout_url) throw new Error(d.error || d.message || "Não foi possível gerar o pagamento.");
      // Vai DIRETO para o gateway, como o checkout de livro. O crédito só entra
      // quando o pagamento confirma — nunca na criação do pedido.
      window.location.href = d.checkout_url;
    } catch (e) {
      message.error(e.message, 6);
      setOcupado(false);
    }
  };

  const comprarComCoins = async (p) => {
    const token = localStorage.getItem(tokenKey);
    if (!token) return message.error("Entre na sua conta para comprar.");

    // O clique no "500" É o gasto — não há tela de pagamento depois dele para
    // desistir. Ver ui/ConfirmarGasto.
    const saldoCoins = Number(dados?.saldo?.coins) || 0;
    const ok = await confirmar({
      tom: "coin",
      titulo: "Trocar Coins por Tickets",
      linhas: [
        { rotulo: "Você paga", valor: <Coins n={p.preco_coins} /> },
        { rotulo: "Você recebe", valor: <><Tickets n={p.tickets} /> · {p.dias} dias</> },
        { rotulo: "Suas Coins", valor: <>{saldoCoins} → <Coins n={Math.max(0, saldoCoins - p.preco_coins)} /></> },
      ],
      nota: "Os Tickets ficam guardados na sua conta: cada um vira 30 dias de Clube quando você decidir usar.",
      textoConfirmar: "Confirmar troca",
    });
    if (!ok) return;

    const quantidade = p.tickets;
    setOcupado(true);
    try {
      const r = await fetch(API_URL + "creditos/tickets-com-coins", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body: JSON.stringify({ quantidade }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Não foi possível concluir a troca.");
      const t = (n) => `${n} ${Number(n) === 1 ? "Ticket" : "Tickets"}`;
      message.success(`${t(d.comprados)} na sua conta. Saldo: ${t(d.tickets)} e ${d.coins} Coins.`, 6);
      await carregar();
      // O cabeçalho do perfil mostra os dois saldos: sem este aviso ele
      // continuaria com o número de antes até a próxima navegação.
      window.dispatchEvent(new CustomEvent("creditosAtualizados"));
    } catch (e) {
      message.error(e.message, 6);
    } finally {
      setOcupado(false);
    }
  };

  if (carregando) {
    return <p className="text-sm text-white/60">Carregando…</p>;
  }

  if (erro && !dados) {
    return (
      <div className="bloco p-6">
        <p className="text-sm text-white/70">{erro}</p>
      </div>
    );
  }

  const { saldo, pacotes, dias_por_ticket: diasPorTicket } = dados;
  const melhorTicket = idDoMelhor(pacotes.tickets, (p) => p.tickets);
  const melhorCoin = idDoMelhor(pacotes.coins, (p) => p.coins);

  // `lg:grid-cols-4`, e não `md:`: com os dois botões lado a lado o card
  // precisa de ~125px por dentro, e entre 768 e 1023px a coluna do perfil
  // (descontada a barra lateral de 280px) só dá isso em DUAS colunas.
  const grade = "grid grid-cols-2 lg:grid-cols-4 gap-x-3 gap-y-5 orfa-ao-centro-ate-lg";

  return (
    <div>
      <h2 className="titulo-display text-[2.1rem] md:text-[2.5rem] mb-1">Loja de Créditos</h2>
      <p className="text-sm text-white/55 mb-6">
        <Ticket plural={false} /> vira {diasPorTicket} dias de Clube, e os dias somam.{" "}
        <Coin plural={false} /> vale desconto na hora de comprar livro.
      </p>

      {/* Os dois saldos, no alto: é a primeira pergunta de quem abre esta tela. */}
      <div className="mb-8 flex flex-wrap gap-3">
        <div className="flex items-center gap-2.5 cartao px-4 py-3">
          <i className="bi bi-ticket-perforated-fill text-2xl text-insigne-ticket" aria-hidden="true" />
          <div>
            <p className="text-xl font-extrabold text-insigne-ticket leading-none">{saldo.tickets}</p>
            <p className="text-[11px] text-white/50">
              <Ticket plural={saldo.tickets !== 1} /> · {saldo.tickets * diasPorTicket} dias
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2.5 cartao px-4 py-3">
          <i className="bi bi-coin text-2xl text-insigne-orange" aria-hidden="true" />
          <div>
            <p className="text-xl font-extrabold text-insigne-orange leading-none">{saldo.coins}</p>
            <p className="text-[11px] text-white/50"><Coin plural={saldo.coins !== 1} /></p>
          </div>
        </div>
      </div>

      {/* ===== Tickets em cima ===== */}
      <h3 className="flex items-center gap-2 text-base font-bold mb-1">
        <i className="bi bi-ticket-perforated-fill text-insigne-ticket" />
        <span><span className="text-insigne-ticket">Tickets</span> do Clube</span>
      </h3>
      <p className="text-xs text-white/45 mb-4">
        Cada <Ticket plural={false} /> vale {diasPorTicket} dias. Compre vários e use quando quiser: eles ficam guardados.
      </p>
      <div className={grade + " mb-9"}>
        {pacotes.tickets.map((p, i) => (
          <Card
            key={p.id}
            icone="bi-ticket-perforated-fill"
            cor="text-insigne-ticket"
            quantidade={p.tickets}
            rotulo={<Ticket plural={p.tickets !== 1} />}
            detalhe={`${p.dias} dias de Clube`}
            centavos={p.centavos}
            deCentavos={p.de_centavos}
            pct={p.desconto_percentual}
            indiceTarja={i}
            melhor={p.id === melhorTicket}
            corDestaque="ticket"
            aoComprar={() => comprar("ticket", p.tickets)}
            precoCoins={p.preco_coins}
            podeComCoins={saldo.coins >= p.preco_coins}
            aoComprarComCoins={() => comprarComCoins(p)}
            ocupado={ocupado}
          />
        ))}
      </div>

      {/* ===== Coins embaixo ===== */}
      <h3 className="flex items-center gap-2 text-base font-bold mb-1">
        <i className="bi bi-coin text-insigne-orange" />
        <span className="text-insigne-orange">Insigne Coins</span>
      </h3>
      <p className="text-xs text-white/45 mb-4">
        Viram desconto na hora de comprar livro, e também compram <Ticket />.
      </p>
      <div className={grade}>
        {pacotes.coins.map((p, i) => (
          <Card
            key={p.id}
            icone="bi-coin"
            cor="text-insigne-orange"
            quantidade={p.coins}
            rotulo={<Coin plural={p.coins !== 1} />}
            centavos={p.centavos}
            deCentavos={p.de_centavos}
            pct={p.desconto_percentual}
            // Deslocado em 4 na lista: a fileira de Coins fica logo abaixo da
            // de Tickets, e com o mesmo índice as tarjas das duas sairiam
            // alinhadas no mesmo ângulo, coluna por coluna — o carimbo que a
            // lista existe para evitar.
            indiceTarja={i + 4}
            melhor={p.id === melhorCoin}
            corDestaque="coin"
            aoComprar={() => comprar("coin", p.coins)}
            ocupado={ocupado}
          />
        ))}
      </div>

      {dialogo}
    </div>
  );
};

export default Creditos;
